// src/pages/container_rental/components/timeline/RentalTimeline.tsx
//
// 간트(타임라인) 뷰 컨테이너.
//
// 원칙
//  - 리스트가 이미 들고 있는 items 를 재사용하지 않는다. 타임라인은 페이징이
//    없는 슬림 DTO 를 자기가 직접 조회한다(getTimeline).
//  - 오늘선·연체 판정의 진실은 서버 range.today(KST) 하나뿐이다.
//  - 페이지 body 가 가로로 밀리면 안 된다 → 차트 컨테이너만 overflow-x: auto,
//    라벨 컬럼 sticky left, 날짜 헤더 sticky top.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Snackbar } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import type { Dayjs } from "dayjs";

import { designTokens } from "../../../../theme.ts";
import { parseKst, todayKst, toIsoDate, fmtDate } from "../../../../utils/date.ts";
import { useContainerRentalApi } from "../../../../hooks/useContainerRentalApi.ts";
import type { RentalStatus } from "../../../../contracts/container_rental/types";
import type {
  TimelineResponse,
  TimelineRowDto,
} from "../../../../contracts/container_rental/timeline";
import { MARK } from "../statusVisuals.ts";
import TimelineDefs from "./TimelineDefs.tsx";
import TimelineHeader, { HEADER_H } from "./TimelineHeader.tsx";
import TimelineLegend from "./TimelineLegend.tsx";
import TimelineRow from "./TimelineRow.tsx";
import TimelineToolbar from "./TimelineToolbar.tsx";
import type { RangePreset } from "./TimelineToolbar.tsx";
import type { ZoomLevel } from "./TimelineScale.ts";
import {
  dateToX,
  fitRange,
  pxPerDay,
  ticksForZoom,
  trackWidth,
  xToDate,
  zoomIn,
  zoomOut,
} from "./TimelineScale.ts";
import { barRange, deriveKpi } from "./timelineModel.ts";

const T = designTokens;

/** 확정 디자인 시스템의 중립 토큰(designTokens 에 없는 보더/서브/필드 배경). */
const C = {
  surface: "#FFFFFF",
  field: "#F9FAFB",
  border: "#E4E7EA",
  ink: T.ink,
  sub: "#6B7178",
  caption: "#9AA0A6",
  neutral: "#F2F4F5",
  accentTint: "#FDEDE8",
  accentHover: "#C93A14",
};

const LABEL_W = 236;
const ROW_H = 62;
/**
 * 차트 높이는 고정 상한 대신 "화면에 남은 높이"를 쓴다(measureAvail).
 * 첫 렌더에서 아직 측정 전일 때만 이 값을 임시로 쓴다.
 */
const FALLBACK_VIEWPORT_H = 620;
const MIN_VIEWPORT_H = 240;
/** 차트 아래 여백 몫 — 페이지 하단 패딩(md 24px) + 숨 고를 여유. */
const BOTTOM_PAD = 32;
/** 이 행 수를 넘으면 세로 윈도잉을 켠다. 도메인이 넓으면 행 하나도 비싸다. */
const VIRTUAL_THRESHOLD = 40;
const OVERSCAN = 10;
const DRAG_THRESHOLD = 5;
/**
 * 가로 컬링 창. scrollLeft 를 CULL_STEP 로 양자화해서 상태 갱신 횟수를 줄이고,
 * 창 좌우로 CULL_PAD(> CULL_STEP) 를 더 그려 두어 양자화 사이 구간에서도
 * 세그먼트가 비어 보이지 않게 한다.
 */
const CULL_STEP = 400;
const CULL_PAD = 700;

interface RentalTimelineProps {
  /** RentalSearchBar 가 들고 있는 검색어 — 리스트와 공유 */
  q?: string;
  status?: RentalStatus | "";
  /** 조회 실패 시 다른 뷰(카드/발행 목록)로 되돌리기 */
  onFallbackToCards?: () => void;
  /** onFallbackToCards 버튼 라벨. 호스트 페이지마다 의미가 달라 주입받는다. */
  fallbackLabel?: string;
  /** 빈 상태에서 신규 등록으로 보낼 때. 없으면 버튼을 그리지 않는다. */
  onCreateNew?: () => void;
}

export default function RentalTimeline({
  q = "",
  status = "",
  onFallbackToCards,
  fallbackLabel = "카드 뷰로 보기",
  onCreateNew,
}: RentalTimelineProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const inDashboard = location.pathname.startsWith("/dashboard");
  const basePath = inDashboard ? "/dashboard/container-rental" : "/container-rental";

  const { getTimeline } = useContainerRentalApi();

  const [data, setData] = useState<TimelineResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: "error" | "success" } | null>(null);

  const [zoom, setZoom] = useState<ZoomLevel>("quarter");
  const [preset, setPreset] = useState<RangePreset | "custom">("all");
  const [fromInput, setFromInput] = useState("");
  const [toInput, setToInput] = useState("");
  const [applied, setApplied] = useState<{ from: string; to: string }>({ from: "", to: "" });
  const [overdueOnly, setOverdueOnly] = useState(false);

  const [scrollTop, setScrollTop] = useState(0);
  const [cull, setCull] = useState<{ from: number; to: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  /** 차트가 쓸 수 있는 화면 높이 — 창 크기·상단 요소 높이에서 실측한다. */
  const [availH, setAvailH] = useState(FALLBACK_VIEWPORT_H);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const legendRef = useRef<HTMLDivElement | null>(null);
  /** 응답 순번 — 늦게 도착한 이전 응답이 최신 결과를 덮지 않게 한다. */
  const reqSeqRef = useRef(0);
  const dragRef = useRef<{
    x: number;
    y: number;
    sl: number;
    st: number;
    moved: number;
    captured: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);
  const centerTodayRef = useRef(true);
  const zoomAnchorRef = useRef<Dayjs | null>(null);

  /* ---------------- 조회 ---------------- */

  // 기간 입력은 타이핑 중에도 change 가 튀므로 debounce 후 적용한다.
  useEffect(() => {
    const id = setTimeout(() => {
      const next = { from: fromInput, to: toInput };
      setApplied((prev) =>
        prev.from === next.from && prev.to === next.to ? prev : next
      );
    }, 350);
    return () => clearTimeout(id);
  }, [fromInput, toInput]);

  const fetchTimeline = useCallback(
    async (params: { q: string; status: RentalStatus | ""; from: string; to: string }) => {
      // q·status·기간을 빠르게 바꾸면 응답이 순서를 지키지 않는다. 타임라인은
      // 페이징 없이 전체를 교체하므로, 뒤늦게 도착한 이전 응답은 통째로 버린다.
      const seq = (reqSeqRef.current += 1);
      const stale = () => seq !== reqSeqRef.current;

      setLoading(true);
      setError(null);
      try {
        // 계약: from/to 는 "둘 다" 있을 때만 전송한다.
        const bothSet = Boolean(params.from && params.to);
        const res = await getTimeline({
          q: params.q || undefined,
          status: params.status || undefined,
          from: bothSet ? params.from : undefined,
          to: bothSet ? params.to : undefined,
        });
        if (stale()) return;
        setData(res);
      } catch (e: any) {
        if (stale()) return;
        const msg = e?.message ?? "타임라인을 불러오지 못했습니다.";
        setError(msg);
        setSnack({ msg, severity: "error" });
      } finally {
        // return 으로 빠져나와도 finally 는 실행된다 — 최신 요청일 때만 끈다.
        if (!stale()) setLoading(false);
      }
    },
    // useApi 의 apiFetch 는 매 렌더 새로 만들어지므로 의존성에 넣지 않는다
    // (listRentals 를 쓰는 ContainerRentalListPage.fetchList 와 동일한 관례).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    fetchTimeline({ q, status, from: applied.from, to: applied.to });
  }, [q, status, applied.from, applied.to, fetchTimeline]);

  /* ---------------- 파생값 ---------------- */

  const rows: TimelineRowDto[] = useMemo(() => data?.rows ?? [], [data]);

  /** 오늘선·연체의 유일한 진실: 서버 range.today. 없으면 클라 KST 로 폴백. */
  const today = useMemo(
    () => parseKst(data?.range?.today) ?? todayKst(),
    [data]
  );

  const domain = useMemo(
    () =>
      fitRange({
        today,
        bounds: rows.map((r) => barRange(r, today)),
        requestedFrom: parseKst(applied.from),
        requestedTo: parseKst(applied.to),
      }),
    [rows, today, applied.from, applied.to]
  );

  const ppd = pxPerDay(zoom);
  const chartWidth = useMemo(
    () => trackWidth(domain.from, domain.to, ppd),
    [domain, ppd]
  );

  // 기간을 지정하면 도메인이 그 창으로 제한되므로 오늘이 도메인 밖일 수 있다.
  // 밖이면 오늘선을 그리지 않는다(트랙 밖/라벨 위에 그어지는 것을 막는다).
  const todayInDomain =
    today.valueOf() >= domain.from.valueOf() &&
    today.valueOf() <= domain.to.valueOf();
  const todayX = useMemo(
    () => (todayInDomain ? dateToX(domain.from, today, ppd) : null),
    [domain, today, ppd, todayInDomain]
  );
  const ticks = useMemo(
    () => ticksForZoom(zoom, domain.from, domain.from, domain.to),
    [zoom, domain]
  );

  const kpi = useMemo(() => deriveKpi(rows, today), [rows, today]);

  const visibleRows = useMemo(
    () => (overdueOnly ? rows.filter((r) => (r.overdueCount || 0) > 0) : rows),
    [rows, overdueOnly]
  );

  // 차트는 화면에 남은 높이(availH)까지 커진다. 행이 적을 때는 내용 높이에
  // 맞춰 줄여서 흰 여백만 남는 큰 상자가 되지 않게 한다.
  const contentH = HEADER_H + visibleRows.length * ROW_H;
  const viewportH = Math.max(MIN_VIEWPORT_H, Math.min(contentH, availH));

  const virtual = visibleRows.length > VIRTUAL_THRESHOLD;
  const firstIdx = virtual
    ? Math.max(0, Math.floor((scrollTop - HEADER_H) / ROW_H) - OVERSCAN)
    : 0;
  const lastIdx = virtual
    ? Math.min(
        visibleRows.length,
        Math.ceil((scrollTop - HEADER_H + viewportH) / ROW_H) + OVERSCAN
      )
    : visibleRows.length;
  const windowRows = virtual ? visibleRows.slice(firstIdx, lastIdx) : visibleRows;

  /* ---------------- 차트 높이 실측 ---------------- */

  // 차트 상단의 화면 좌표에서 창 아래까지가 차트가 쓸 수 있는 높이다.
  // 툴바·검색바·페이지 헤더 높이를 하드코딩하지 않으므로, 위쪽 요소가
  // 늘어나거나(필터 칩 줄바꿈) 줄어도 저절로 맞는다.
  // 차트 높이가 바뀌어도 차트의 top 은 변하지 않으므로 되먹임 루프는 없다.
  const measureAvail = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const legendH = legendRef.current?.offsetHeight ?? 0;
    const next = Math.max(
      MIN_VIEWPORT_H,
      Math.round(window.innerHeight - top - legendH - BOTTOM_PAD)
    );
    // 1~2px 흔들림으로 리렌더가 반복되지 않게 문턱을 둔다.
    setAvailH((prev) => (Math.abs(prev - next) < 8 ? prev : next));
  }, []);

  useLayoutEffect(() => {
    measureAvail();
  }, [measureAvail, loading, error, visibleRows.length, zoom]);

  useEffect(() => {
    window.addEventListener("resize", measureAvail);
    return () => window.removeEventListener("resize", measureAvail);
  }, [measureAvail]);

  /* ---------------- 스크롤 / 팬 / 줌 ---------------- */

  const chartViewportW = () => {
    const el = scrollRef.current;
    if (!el) return 800;
    return Math.max(el.clientWidth - LABEL_W, 120);
  };

  /** 화면에 보이는 가로 구간(트랙 좌표)을 양자화해서 세그먼트 컬링에 쓴다. */
  const updateCull = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const view = Math.max(el.clientWidth - LABEL_W, 120);
    const bucket = Math.floor(el.scrollLeft / CULL_STEP) * CULL_STEP;
    const next = { from: bucket - CULL_PAD, to: bucket + view + CULL_PAD };
    setCull((prev) =>
      prev && prev.from === next.from && prev.to === next.to ? prev : next
    );
  }, []);

  const scrollToToday = useCallback(
    (smooth: boolean) => {
      const el = scrollRef.current;
      if (!el) return;
      // 오늘이 도메인 밖이면(기간을 좁게 지정한 경우) 가까운 끝으로 붙인다.
      const target =
        todayX ?? (today.valueOf() < domain.from.valueOf() ? 0 : chartWidth);
      const left = Math.max(target - chartViewportW() * 0.25, 0);
      el.scrollTo({ left, behavior: smooth ? "smooth" : "auto" });
    },
    [todayX, today, domain, chartWidth]
  );

  // 최초 로드 / 기간 변경 후 오늘선을 좌측 1/4 로.
  useLayoutEffect(() => {
    if (!centerTodayRef.current) return;
    if (loading || !data) return;
    // 차트가 아직(또는 빈 상태라) 렌더되지 않았으면 플래그를 소모하지 않는다.
    // 이후 행이 생겼을 때 오늘선 정렬을 놓치지 않기 위함.
    if (!scrollRef.current) return;
    centerTodayRef.current = false;
    scrollToToday(false);
  }, [loading, data, chartWidth, scrollToToday]);

  // 도메인/줌/행 수가 바뀌면 컬링 창을 다시 잰다(스크롤이 안 일어나도 필요).
  useLayoutEffect(() => {
    updateCull();
  }, [updateCull, chartWidth, ppd, visibleRows.length, data]);

  // 창 크기가 바뀌면 보이는 폭이 달라지므로 컬링 창도 다시 잰다.
  useEffect(() => {
    const onResize = () => updateCull();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [updateCull]);

  // 줌 전환 — 뷰포트 중앙 날짜 앵커 유지.
  useLayoutEffect(() => {
    const anchor = zoomAnchorRef.current;
    if (!anchor) return;
    zoomAnchorRef.current = null;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft = Math.max(
      dateToX(domain.from, anchor, ppd) - chartViewportW() * 0.5,
      0
    );
    updateCull();
    // domain/ppd 는 zoom 과 함께 갱신된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  const captureZoomAnchor = () => {
    const el = scrollRef.current;
    if (!el) return;
    zoomAnchorRef.current = xToDate(
      domain.from,
      el.scrollLeft + chartViewportW() * 0.5,
      ppd
    );
  };

  const handleZoomChange = (next: ZoomLevel) => {
    if (next === zoom) return;
    captureZoomAnchor();
    setZoom(next);
  };

  const handleZoomStep = (dir: -1 | 1) => {
    const next = dir > 0 ? zoomIn(zoom) : zoomOut(zoom);
    if (next === zoom) return;
    captureZoomAnchor();
    setZoom(next);
  };

  const handlePan = (dir: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * chartViewportW() * 0.9, behavior: "smooth" });
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest?.("input, textarea, select, button, a")) return;
    const el = scrollRef.current;
    if (!el) return;
    suppressClickRef.current = false;
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      sl: el.scrollLeft,
      st: el.scrollTop,
      moved: 0,
      captured: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    // 버튼을 뗀 채로 움직이고 있다면 남아 있는 드래그 상태를 정리한다.
    // 5px(=DRAG_THRESHOLD) 를 넘기 전에는 포인터 캡처가 없어서, 컨테이너 밖에서
    // 버튼을 떼면 pointerup/pointercancel 이 오지 않는다. 그대로 두면 다음에
    // 버튼을 누르지 않고 차트 위를 지나가기만 해도 옛 시작좌표로 유령 팬이
    // 일어나고, 이어지는 클릭 1회가 onClickCapture 에 먹힌다.
    if (e.buttons === 0) {
      if (d) {
        dragRef.current = null;
        setDragging(false);
      }
      return;
    }
    const el = scrollRef.current;
    if (!d || !el) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    d.moved = Math.max(d.moved, Math.sqrt(dx * dx + dy * dy));
    if (d.moved < DRAG_THRESHOLD) return;

    // 5px 를 넘긴 뒤에야 포인터를 캡처한다 — 그 전에 캡처하면 세그먼트 클릭이
    // 컨테이너로 리타깃되어 팝오버가 열리지 않는다.
    if (!d.captured) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
        d.captured = true;
      } catch {
        /* 캡처 실패해도 팬 자체는 동작한다 */
      }
      setDragging(true);
    }
    suppressClickRef.current = true;
    el.scrollLeft = d.sl - dx;
    el.scrollTop = d.st - dy;
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (d?.captured) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
    }
    dragRef.current = null;
    setDragging(false);
    if (suppressClickRef.current) {
      // 드래그 직후의 click 은 한 번만 삼킨다. click 은 pointerup 과 같은 태스크
      // 안에서 오므로 다음 태스크에서 플래그를 푼다. 컨테이너 밖에서 버튼을 떼어
      // click 이 아예 오지 않는 경우에도 플래그가 남아 다음 클릭을 먹지 않는다.
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }
  };

  // 캡처 이전(5px 미만)에 포인터가 컨테이너를 벗어나면 여기서 정리한다.
  // 캡처된 뒤에는 컨테이너 밖에서도 팬이 이어져야 하므로 건드리지 않는다.
  const onPointerLeave = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || d.captured) return;
    endDrag(e);
  };

  const onClickCapture = (e: React.MouseEvent) => {
    if (!suppressClickRef.current) return;
    suppressClickRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    updateCull();
    if (!virtual) return;
    setScrollTop(e.currentTarget.scrollTop);
  };

  /* ---------------- 기간 컨트롤 ---------------- */

  const handleRangeChange = (nextFrom: string, nextTo: string) => {
    centerTodayRef.current = true;
    setPreset("custom");
    setFromInput(nextFrom);
    setToInput(nextTo);
  };

  const handlePresetChange = (p: RangePreset) => {
    centerTodayRef.current = true;
    setPreset(p);
    if (p === "all") {
      setFromInput("");
      setToInput("");
      return;
    }
    const months = p === "3m" ? 3 : p === "6m" ? 6 : 12;
    setFromInput(toIsoDate(today.startOf("month")));
    setToInput(toIsoDate(today.add(months, "month").endOf("month")));
  };

  const handleOpenDetail = (id: string) => navigate(`${basePath}/${id}`);

  /* ---------------- 렌더 ---------------- */

  const isEmpty = !loading && !error && visibleRows.length === 0;

  return (
    <Box sx={{ maxWidth: "100%", overflowX: "hidden" }}>
      <TimelineDefs />

      <TimelineToolbar
        zoom={zoom}
        onZoomChange={handleZoomChange}
        onZoomStep={handleZoomStep}
        onPan={handlePan}
        onToday={() => scrollToToday(true)}
        from={fromInput}
        to={toInput}
        onRangeChange={handleRangeChange}
        preset={preset}
        onPresetChange={handlePresetChange}
        overdueOnly={overdueOnly}
        onOverdueOnlyChange={setOverdueOnly}
        kpi={kpi}
        todayLabel={fmtDate(today)}
      />

      <Box
        sx={{
          position: "relative",
          border: `1px solid ${C.border}`,
          borderRadius: "12px",
          boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
          bgcolor: T.paper,
          overflow: "hidden",
        }}
      >
        {/* 차트 헤더 */}
        <Box
          sx={{
            bgcolor: C.field,
            borderBottom: `1px solid ${C.border}`,
            color: C.ink,
            px: 2,
            py: 1.15,
            fontFamily: T.fonts.sans,
            fontSize: 13,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <span>발행 일정</span>
          <span
            style={{
              color: C.sub,
              fontWeight: 500,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {visibleRows.length}건{overdueOnly ? " · 연체만" : ""}
          </span>
        </Box>

        {/* 로딩 */}
        {loading && !data && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              py: 8,
            }}
          >
            <CircularProgress size={28} sx={{ color: T.cargo }} />
            <Box sx={{ fontFamily: T.fonts.sans, fontSize: 13, color: C.sub }}>
              타임라인을 불러오는 중입니다…
            </Box>
          </Box>
        )}

        {/* 에러 */}
        {error && !loading && (
          <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
            <Box
              sx={{
                fontFamily: T.fonts.sans,
                fontSize: 15,
                fontWeight: 700,
                color: T.stop,
                mb: 1,
              }}
            >
              타임라인을 불러오지 못했습니다
            </Box>
            <Box sx={{ fontFamily: T.fonts.sans, fontSize: 13, color: C.sub, mb: 2.5 }}>
              {error}
            </Box>
            <Box sx={{ display: "inline-flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={() =>
                  fetchTimeline({ q, status, from: applied.from, to: applied.to })
                }
              >
                다시 시도
              </Button>
              {onFallbackToCards && (
                <Button
                  variant="outlined"
                  startIcon={<ViewModuleIcon sx={{ fontSize: 17 }} />}
                  onClick={onFallbackToCards}
                >
                  {fallbackLabel}
                </Button>
              )}
            </Box>
          </Box>
        )}

        {/* 빈 상태 */}
        {isEmpty && (
          <Box sx={{ py: 7, px: 2, textAlign: "center" }}>
            <Box sx={{ fontFamily: T.fonts.sans, fontSize: 14, color: C.sub }}>
              {overdueOnly
                ? "연체된 회차가 없습니다"
                : "표시할 일정이 없습니다"}
            </Box>
            {onCreateNew && (
              <Box sx={{ mt: 2.5 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  onClick={onCreateNew}
                >
                  신규 등록
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* 서버 상한 초과 안내 — 상위 일부만 내려온 상태 */}
        {!error && data?.truncated && (
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 1,
              px: 2,
              py: 0.75,
              borderBottom: `1px solid ${C.border}`,
              bgcolor: "#FCF3E4",
            }}
          >
            <Box
              sx={{
                fontFamily: T.fonts.sans,
                fontSize: 12.5,
                fontWeight: 700,
                color: "#8A5C12",
                whiteSpace: "nowrap",
              }}
            >
              일부만 표시
            </Box>
            <Box sx={{ fontFamily: T.fonts.sans, fontSize: 12.5, color: C.sub }}>
              조건에 맞는 계약이 많아 시작일 순 상위 {data.rows.length}건만
              표시 중입니다 — 검색어나 기간으로 범위를 좁혀 주세요.
            </Box>
          </Box>
        )}

        {/* 차트 */}
        {!error && visibleRows.length > 0 && (
          <Box
            ref={scrollRef}
            onScroll={handleScroll}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onPointerLeave={onPointerLeave}
            onClickCapture={onClickCapture}
            sx={{
              position: "relative",
              height: viewportH,
              overflowX: "auto",
              overflowY: "auto",
              overscrollBehaviorX: "contain",
              cursor: dragging ? "grabbing" : "grab",
              bgcolor: T.paper,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: LABEL_W + chartWidth,
                minWidth: "100%",
              }}
            >
              {/* 오늘선 — 라벨 컬럼(z 3)·헤더(z 5) 아래, 바 위.
                  도메인 밖(기간을 좁게 지정한 경우)이면 그리지 않는다. */}
              {todayX !== null && (
                <Box
                  sx={{
                    position: "absolute",
                    left: LABEL_W + todayX,
                    top: 0,
                    bottom: 0,
                    width: 0,
                    borderLeft: `2px solid ${MARK.today}`,
                    zIndex: 1,
                    pointerEvents: "none",
                  }}
                />
              )}

              {/* 날짜 헤더 (sticky top) */}
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  display: "flex",
                  alignItems: "stretch",
                  bgcolor: T.paper,
                }}
              >
                <Box
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 6,
                    flex: "0 0 auto",
                    width: LABEL_W,
                    height: HEADER_H,
                    bgcolor: C.field,
                    color: C.ink,
                    // 우측·하단 하이라인은 TimelineHeader / TimelineRow 가 긋는
                    // 선과 이어져야 해서 같은 시스템 보더(#E4E7EA)를 쓴다.
                    borderRight: `1px solid ${C.border}`,
                    borderBottom: `1px solid ${C.border}`,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 0.25,
                    px: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: T.fonts.sans,
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: C.ink,
                    }}
                  >
                    계약
                  </Box>
                  <Box sx={{ fontFamily: T.fonts.sans, fontSize: 11, color: C.sub }}>
                    거래처 · 현장
                  </Box>
                </Box>

                <TimelineHeader
                  width={chartWidth}
                  top={ticks.top}
                  bottom={ticks.bottom}
                  bottomKind={ticks.bottomKind}
                  bottomLabelled={ticks.bottomLabelled}
                  todayX={todayX}
                />
              </Box>

              {/* 행 */}
              <Box
                sx={{
                  pt: `${firstIdx * ROW_H}px`,
                  pb: `${Math.max(visibleRows.length - lastIdx, 0) * ROW_H}px`,
                }}
              >
                {windowRows.map((row) => (
                  <TimelineRow
                    key={row.id}
                    row={row}
                    origin={domain.from}
                    domainEnd={domain.to}
                    ppd={ppd}
                    today={today}
                    chartWidth={chartWidth}
                    labelWidth={LABEL_W}
                    rowHeight={ROW_H}
                    viewFrom={cull?.from ?? null}
                    viewTo={cull?.to ?? null}
                    onOpenDetail={handleOpenDetail}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* 실측용 래퍼 — 범례 높이만큼을 차트 높이에서 뺀다(measureAvail). */}
      <Box ref={legendRef}>
        <TimelineLegend />
      </Box>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            onClose={() => setSnack(null)}
            variant="filled"
            sx={{ width: "100%", borderRadius: "8px", fontFamily: T.fonts.sans }}
          >
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

